import React from 'react'

import {toString} from 'es-toolkit/compat'
import {useFormik} from 'formik'
import {useNavigate} from 'react-router'
import * as Yup from 'yup'

import {useAppDispatch} from '../../hooks/redux'
import {fatalError} from '../../redux/features/core'
import {openCreatePricingModal} from '../../redux/features/modal'

import useAsync from '../../../../commons/src/hooks/use-async'
import * as priceApi from '../../services/api/price'
import * as providerInfoApi from '../../services/api/provider-info'
import {goToEditProviderInfo} from '../../services/navigation'
import {
  PossiblyMixedValue,
  isMixedValue,
  toMixedValidationSchema,
  toPossiblyMixedValue,
  toTouchedFormData,
} from '../utils/form'

import {EditProviderInfoTab, Price, PriceId, ProviderId, ProviderInfo} from '../../types'

import FieldNote from '../../../../commons/src/components/field-note'
import FormRow from '../../../../commons/src/components/form-row'
import Link from '../../../../commons/src/components/link'
import Paragraph from '../../../../commons/src/components/paragraph'
import SelectField from '../../../../commons/src/components/select-field'
import SelectMenu from '../../../../commons/src/components/select-menu'
import Stack from '../../../../commons/src/components/stack'

import SaveButtonPartial from '../save-button-partial'

type Props = {
  providerId: ProviderId
  providerInfos: Array<ProviderInfo>
}

export type PriceOption = {
  label: string
  value: PriceId
}

export type SingleFormData = {
  priceId: string
  formikGeneral?: unknown // Need type to be able to set general validation error.
}

type MixedFormData = {
  priceId: PossiblyMixedValue<string>
  formikGeneral?: unknown // Need type to be able to set general validation error.
}

export const initialValues = {
  priceId: '',
}

export const validationSchema = {
  priceId: Yup.string().label('Pricing').required(),
}

export const pricingsToFormData = (providerInfos: ProviderInfo[]): MixedFormData => ({
  priceId: toPossiblyMixedValue(
    providerInfos.map(providerInfo => providerInfo.priceId),
    toString
  ),
})

const EditPricingPartial = ({providerId, providerInfos}: Props) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [isSubmitted, setIsSubmitted] = React.useState(false)

  const {
    isLoading: isLoadingPrices,
    data: prices = [],
    refresh,
  } = useAsync(() => priceApi.getByProvider(providerId), [], {
    onError: error => dispatch(fatalError({error})),
  })

  const formik = useFormik<MixedFormData>({
    enableReinitialize: true,
    initialValues: {
      ...initialValues,
      ...pricingsToFormData(providerInfos),
    },
    validationSchema: toMixedValidationSchema(validationSchema),
    onSubmit: async (values, actions) => {
      const providerInfoRequest = toTouchedFormData(
        values,
        formik.initialValues
      ) as Partial<SingleFormData>

      try {
        const providerInfoIds = providerInfos.map(info => info.id)
        const nextProviderInfos = await providerInfoApi.patchMultiple(
          providerInfoRequest,
          providerInfoIds
        )

        goToEditProviderInfo(navigate, {
          providerInfos: nextProviderInfos,
          activeTab: EditProviderInfoTab.PRICING,
          replace: true,
        })

        setIsSubmitted(true)
      } catch (error) {
        actions.setFieldError('formikGeneral', (error as Error).message)
      }
    },
  })

  const setCreatedPricing = (price: Price) => {
    refresh()
    formik.setFieldValue('priceId', price.id)
  }

  const priceOptions: PriceOption[] = prices.map(({id, name}) => ({
    label: name,
    value: id,
  }))

  return (
    <form onSubmit={formik.handleSubmit}>
      {formik.errors.formikGeneral && <Paragraph error>{formik.errors.formikGeneral}</Paragraph>}
      <Paragraph>
        You can either select one of your existing pricings or you can{' '}
        <Link onClick={() => dispatch(openCreatePricingModal({onDone: setCreatedPricing}))}>
          create a new one
        </Link>
        .
      </Paragraph>
      <Stack space="300">
        <FormRow>
          <FieldNote appearance="error" note={formik.submitCount > 0 && formik.errors.priceId}>
            <SelectField
              name="type"
              disabled={isLoadingPrices}
              placeholder={isLoadingPrices ? 'Loading pricings...' : 'Select pricing'}
              mixedLabel="Mixed"
              mixed={isMixedValue(formik.values.priceId)}
              value={priceOptions.find(({value}) => value === formik.values.priceId)}
              menu={<SelectMenu values={priceOptions} />}
              onChange={option => {
                formik.setFieldValue('priceId', (option as PriceOption).value)
              }}
              onBlur={formik.handleBlur}
            />
          </FieldNote>
        </FormRow>
        <SaveButtonPartial formik={formik} isSubmitted={isSubmitted} />
      </Stack>
    </form>
  )
}

export default EditPricingPartial
